import { React, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Button, Card, CardBody, Spinner } from 'reactstrap';
import { GoogleLogin } from '@react-oauth/google';
import { decodeToken } from 'react-jwt';
import { AuthContext, estaEnWhitelist } from '../../context/authContext';
import { getItem, STORAGE_KEYS } from '../../services/storage';
import config from '../../config';
import logo from '../../assets/images/logos/logo.png';
import {
  aprobar,
  cancelar,
  EDAD_MAX_LOGIN_S,
  ESPERA_ACTIVACION_MS,
  leerClaimsSesion,
  normalizarEmail,
  redirectToValido,
  resolverApiOrigin,
  uidValido,
} from './mcpLoginApi';
import '../../assets/scss/style.scss';
import './McpLogin.scss';

// Pantalla `/mcp-login?uid=…`: confirma con qué cuenta se conecta el MCP de
// alumnos (servidor OAuth del MCP en apis-node, `POST /oauth/authorize/decision`).
// Reglas de seguridad:
// - «aprobar» solo tras un clic explícito en Continuar, nunca automático.
// - El email mostrado sale del JWT de sesión del panel (el mismo que verá
//   apis-node); si el `email` de la respuesta no coincide, no se navega.
// - Si la sesión cambia entre mostrar y Continuar, se vuelve a mostrar la
//   confirmación con la nueva cuenta (sin llamar a apis-node).
// - `redirectTo` se valida antes de navegar y nunca se guarda ni se registra.
// - Nada (uid, email, JWT, respuestas) se escribe en storage ni en consola.

const NOMBRE_CLIENTE = 'MCP de alumnos';

const ESTADOS = {
  INVALIDA: 'invalida',
  CARGANDO: 'cargando',
  SUPLANTACION: 'suplantacion',
  SIN_SESION: 'sinSesion',
  REAUTENTICACION: 'reautenticacion',
  CONFIRMACION: 'confirmacion',
  DENEGADO: 'denegado',
  CADUCADO: 'caducado',
  EMAIL_DISTINTO: 'emailDistinto',
  ERROR: 'error',
};

// Tipos de error: los reintentables muestran «Reintentar».
const ERRORES = {
  GENERICO: 'generico',
  RED: 'red',
  LIMITE: 'limite',
  NO_DISPONIBLE: 'noDisponible',
};

const MENSAJES_ERROR = {
  [ERRORES.GENERICO]:
    'No se ha podido completar la solicitud. Vuelve a Claude y pulsa Conectar de nuevo.',
  [ERRORES.RED]:
    'No se ha podido contactar con el servidor. Comprueba la conexión e inténtalo de nuevo.',
  [ERRORES.LIMITE]: 'Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.',
  [ERRORES.NO_DISPONIBLE]:
    'El servicio no está disponible en este momento. Inténtalo de nuevo en unos instantes.',
};

const errorPorStatus = (status) => {
  if (status === 429) return ERRORES.LIMITE;
  if (status === 503) return ERRORES.NO_DISPONIBLE;
  return ERRORES.GENERICO;
};

const leerSesion = () => getItem(STORAGE_KEYS.USER);

const Botonera = ({ children }) => (
  <div className="d-flex justify-content-center gap-2 flex-wrap mt-4">{children}</div>
);

Botonera.propTypes = {
  children: PropTypes.node,
};

const AvisoPhishing = () => (
  <p className="mcp-login__aviso p-3 mb-0 text-start small" role="note">
    <strong>Importante:</strong> continúa solo si acabas de iniciar tú la conexión. Si te han
    enviado este enlace, pulsa Cancelar.
  </p>
);

const paginaActiva = () => document.visibilityState === 'visible' && document.hasFocus();

// true solo cuando la página lleva `ESPERA_ACTIVACION_MS` visible y con foco.
// Se reinicia al perder/recuperar foco o visibilidad y cada vez que cambia
// `clave` (la pantalla mostrada): un doble clic en un botón anterior, o en otra
// ventana, no puede caer sobre Continuar o Volver.
const useActivacionSegura = (clave) => {
  const [activa, setActiva] = useState(false);
  const activaRef = useRef(false);

  useEffect(() => {
    let temporizador = null;
    const fijar = (valor) => {
      activaRef.current = valor;
      setActiva(valor);
    };
    const reiniciar = () => {
      clearTimeout(temporizador);
      temporizador = null;
      fijar(false);
      if (!paginaActiva()) return;
      temporizador = setTimeout(() => {
        temporizador = null;
        if (paginaActiva()) fijar(true);
      }, ESPERA_ACTIVACION_MS);
    };

    reiniciar();
    window.addEventListener('focus', reiniciar);
    window.addEventListener('blur', reiniciar);
    document.addEventListener('visibilitychange', reiniciar);
    return () => {
      clearTimeout(temporizador);
      window.removeEventListener('focus', reiniciar);
      window.removeEventListener('blur', reiniciar);
      document.removeEventListener('visibilitychange', reiniciar);
    };
  }, [clave]);

  return [activa, activaRef];
};

const McpLogin = () => {
  const { loginGoogle } = useContext(AuthContext);
  const { search } = useLocation();
  // Solo se lee `uid`; cualquier otro parámetro (p. ej. `code`) se ignora.
  const uid = useMemo(() => new URLSearchParams(search).get('uid'), [search]);
  const apiOrigin = useMemo(() => resolverApiOrigin(config), []);

  const [estado, setEstado] = useState(ESTADOS.CARGANDO);
  const [tipoError, setTipoError] = useState(ERRORES.GENERICO);
  const [cuentaEmail, setCuentaEmail] = useState('');
  const [avisoLogin, setAvisoLogin] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [activa, activaRef] = useActivacionSegura(estado);

  // Token y email de la confirmación que se está mostrando: «aprobar» usa ese
  // token y la respuesta debe traer ese email.
  const tokenMostradoRef = useRef(null);
  const emailMostradoRef = useRef('');
  // Token obtenido con el login hecho en esta pantalla: su `auth_time` es
  // reciente aunque el reloj local diga otra cosa (lo valida apis-node).
  const tokenLoginLocalRef = useRef(null);
  // `redirectTo` ya validado de un 403 acceso_denegado (para «Volver»).
  const redirectDenegadoRef = useRef(null);
  // Guarda contra dobles envíos (el estado `enCurso` llega tarde a un doble clic).
  const enCursoRef = useRef(false);

  const empezar = () => {
    if (enCursoRef.current) return false;
    enCursoRef.current = true;
    setEnCurso(true);
    return true;
  };

  const terminar = () => {
    enCursoRef.current = false;
    setEnCurso(false);
  };

  const mostrarError = useCallback((tipo) => {
    setTipoError(tipo);
    setEstado(ESTADOS.ERROR);
  }, []);

  // Decide qué pantalla toca con la sesión local, sin llamar a apis-node.
  const evaluarSesion = useCallback(() => {
    setAvisoLogin('');
    tokenMostradoRef.current = null;
    emailMostradoRef.current = '';

    const sesion = leerSesion();
    if (sesion?.impersonating === true) {
      setEstado(ESTADOS.SUPLANTACION);
      return;
    }

    const token = sesion?.tokenId;
    const claims = leerClaimsSesion(token);
    const ahora = Math.floor(Date.now() / 1000);
    // Las comprobaciones de reloj no se aplican al token del login hecho aquí: con
    // el reloj local adelantado dejarían a la persona en un bucle de login.
    const esLoginLocal = !!token && token === tokenLoginLocalRef.current;
    if (!claims || (!esLoginLocal && claims.exp !== null && claims.exp <= ahora)) {
      setEstado(ESTADOS.SIN_SESION);
      return;
    }
    if (!esLoginLocal && claims.authTime !== null && ahora - claims.authTime > EDAD_MAX_LOGIN_S) {
      setEstado(ESTADOS.REAUTENTICACION);
      return;
    }

    tokenMostradoRef.current = token;
    emailMostradoRef.current = claims.email;
    setCuentaEmail(claims.email);
    setEstado(ESTADOS.CONFIRMACION);
  }, []);

  useEffect(() => {
    if (!uidValido(uid)) {
      setEstado(ESTADOS.INVALIDA);
      return;
    }
    if (!apiOrigin) {
      mostrarError(ERRORES.GENERICO);
      return;
    }
    evaluarSesion();
  }, [uid, apiOrigin, evaluarSesion, mostrarError]);

  const navegar = (redirectTo) => {
    if (!redirectToValido(redirectTo, apiOrigin)) return false;
    window.location.assign(redirectTo);
    return true;
  };

  const onContinuar = async () => {
    // `disabled` ya lo impide; la ref cubre eventos despachados antes del re-render.
    if (!activaRef.current || !empezar()) return;

    const sesion = leerSesion();
    if (sesion?.impersonating === true) {
      setEstado(ESTADOS.SUPLANTACION);
      terminar();
      return;
    }
    // Si la sesión cambió (otra pestaña) desde que se pintó, se vuelve a mostrar.
    if (!tokenMostradoRef.current || sesion?.tokenId !== tokenMostradoRef.current) {
      evaluarSesion();
      terminar();
      return;
    }

    let respuesta;
    try {
      respuesta = await aprobar(apiOrigin, uid, tokenMostradoRef.current);
    } catch {
      mostrarError(ERRORES.RED);
      terminar();
      return;
    }

    const { status, data } = respuesta;
    if (status === 200) {
      // Defensa en profundidad: la cuenta aprobada debe ser la que se mostró.
      if (!emailMostradoRef.current || normalizarEmail(data?.email) !== emailMostradoRef.current) {
        setEstado(ESTADOS.EMAIL_DISTINTO);
      } else if (navegar(data?.redirectTo)) {
        // Si navega, los botones siguen deshabilitados hasta salir de la página.
        return;
      } else {
        mostrarError(ERRORES.GENERICO);
      }
    } else if (status === 401) {
      if (data?.error === 'reautenticacion_requerida') {
        setEstado(ESTADOS.REAUTENTICACION);
      } else if (tokenMostradoRef.current === tokenLoginLocalRef.current) {
        // sesion_invalida con el token recién obtenido aquí: otro login no lo
        // arreglaría (evita el bucle login → 401 → login).
        mostrarError(ERRORES.GENERICO);
      } else {
        // sesion_invalida: se pide un login nuevo, que sobrescribe `user`. No se
        // llama a logoutGoogle (borraría todo el storage y cerraría el panel en
        // todas las pestañas).
        setEstado(ESTADOS.SIN_SESION);
      }
    } else if (status === 403 && data?.error === 'acceso_denegado') {
      if (redirectToValido(data?.redirectTo, apiOrigin)) {
        redirectDenegadoRef.current = data.redirectTo;
        setEstado(ESTADOS.DENEGADO);
      } else {
        mostrarError(ERRORES.GENERICO);
      }
    } else if (status === 404) {
      setEstado(ESTADOS.CADUCADO);
    } else {
      mostrarError(errorPorStatus(status));
    }
    terminar();
  };

  const onCancelar = async () => {
    if (!empezar()) return;

    let respuesta;
    try {
      respuesta = await cancelar(apiOrigin, uid);
    } catch {
      mostrarError(ERRORES.RED);
      terminar();
      return;
    }

    const { status, data } = respuesta;
    if (status === 200) {
      if (navegar(data?.redirectTo)) return;
      mostrarError(ERRORES.GENERICO);
    } else if (status === 404) {
      setEstado(ESTADOS.CADUCADO);
    } else {
      mostrarError(errorPorStatus(status));
    }
    terminar();
  };

  const onVolver = () => {
    if (!activaRef.current || !empezar()) return;
    if (!navegar(redirectDenegadoRef.current)) {
      mostrarError(ERRORES.GENERICO);
      terminar();
    }
  };

  // Vuelve a la pantalla que corresponda (nunca repite «aprobar» sin otro clic).
  const onReintentar = () => {
    if (!empezar()) return;
    evaluarSesion();
    terminar();
  };

  const onGoogleSuccess = async ({ credential }) => {
    if (!empezar()) return;
    setAvisoLogin('');

    const profileObj = decodeToken(credential);
    // Misma comprobación cosmética de listas que Login.js (la autorización real
    // la hace apis-node en `decision`).
    if (!profileObj || !estaEnWhitelist(profileObj.email)) {
      setAvisoLogin('No puedes acceder con esta cuenta.');
      terminar();
      return;
    }

    // silent: esta pantalla muestra su propio aviso, sin el toast global técnico.
    const ok = await loginGoogle({ tokenId: credential, profileObj }, { silent: true });
    if (ok) {
      // Tras el login se muestra la confirmación: nunca se aprueba sin clic.
      tokenLoginLocalRef.current = leerSesion()?.tokenId ?? null;
      evaluarSesion();
    } else {
      setAvisoLogin('No se ha podido iniciar sesión. Inténtalo de nuevo.');
    }
    terminar();
  };

  const botonCancelar = (
    <Button color="secondary" outline onClick={onCancelar} disabled={enCurso}>
      Cancelar
    </Button>
  );

  const renderLogin = (texto) => (
    <>
      <p className="mb-3">{texto}</p>
      <AvisoPhishing />
      <div className="d-flex justify-content-center mt-4">
        <GoogleLogin
          onSuccess={onGoogleSuccess}
          onError={() => setAvisoLogin('No se ha podido iniciar sesión. Inténtalo de nuevo.')}
          useOneTap={false}
          auto_select={false}
        />
      </div>
      {avisoLogin && (
        <p className="text-danger small mt-3 mb-0" role="alert">
          {avisoLogin}
        </p>
      )}
      <Botonera>{botonCancelar}</Botonera>
    </>
  );

  const renderContenido = () => {
    switch (estado) {
      case ESTADOS.INVALIDA:
        return (
          <p className="mb-0">Solicitud no válida. Vuelve a Claude y pulsa Conectar de nuevo.</p>
        );
      case ESTADOS.CARGANDO:
        return (
          <div className="py-3">
            <Spinner color="primary">Cargando…</Spinner>
          </div>
        );
      case ESTADOS.SUPLANTACION:
        return (
          <>
            <p className="mb-0">
              Estás suplantando a otra persona. Termina la suplantación para conectar el MCP.
            </p>
            <Botonera>{botonCancelar}</Botonera>
          </>
        );
      case ESTADOS.SIN_SESION:
        return renderLogin(
          'Inicia sesión con tu cuenta de cedetecgroup.com para conectar el MCP de alumnos.',
        );
      case ESTADOS.REAUTENTICACION:
        return renderLogin('Por seguridad, vuelve a iniciar sesión para confirmar que eres tú.');
      case ESTADOS.CONFIRMACION:
        return (
          <>
            <p className="mb-3">
              Vas a conectar el <strong>{NOMBRE_CLIENTE}</strong> como{' '}
              <strong>{cuentaEmail}</strong>
            </p>
            <AvisoPhishing />
            <Botonera>
              {botonCancelar}
              <Button color="primary" onClick={onContinuar} disabled={enCurso || !activa}>
                Continuar
              </Button>
            </Botonera>
          </>
        );
      case ESTADOS.DENEGADO:
        return (
          <>
            <p className="mb-0">No tienes acceso al MCP con esta cuenta.</p>
            <Botonera>
              <Button color="primary" onClick={onVolver} disabled={enCurso || !activa}>
                Volver
              </Button>
            </Botonera>
          </>
        );
      case ESTADOS.CADUCADO:
        return (
          <p className="mb-0">
            La solicitud ha caducado o ya se ha usado. Vuelve a Claude y pulsa Conectar de nuevo
            desde este mismo navegador.
          </p>
        );
      case ESTADOS.EMAIL_DISTINTO:
        return (
          <p className="mb-0" role="alert">
            La cuenta validada no coincide con la que se mostraba. Por seguridad, no se ha
            completado la conexión. Vuelve a Claude y pulsa Conectar de nuevo.
          </p>
        );
      default: {
        const reintentable = tipoError !== ERRORES.GENERICO;
        return (
          <>
            <p className="mb-0" role="alert">
              {MENSAJES_ERROR[tipoError]}
            </p>
            {reintentable && (
              <Botonera>
                <Button color="primary" onClick={onReintentar} disabled={enCurso}>
                  Reintentar
                </Button>
              </Botonera>
            )}
          </>
        );
      }
    }
  };

  return (
    <div className="mcp-login d-flex align-items-center justify-content-center p-3">
      <Card className="mcp-login__card w-100">
        <CardBody className="p-4 text-center">
          <img src={logo} alt="Cedetec Group" width="220" className="mx-auto d-block mb-3" />
          <h1 className="mcp-login__titulo mb-4">Conectar el MCP de alumnos</h1>
          {renderContenido()}
        </CardBody>
      </Card>
    </div>
  );
};

export default McpLogin;
